import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';

import {Alignment} from 'analyses/Analyses';
import {assertAndContinue} from 'Asserts';
import staticURL from 'lib/staticURL';
import {MinimalAlignmentRowsView} from 'molbio/maps/alignment/PolymerAlignmentRowsView';
import {SequenceAlignmentRowView} from 'molbio/maps/alignment/SequenceAlignmentRowView';
import SequenceAlignmentTabView from 'molbio/maps/alignment/SequenceAlignmentTabView';
import * as infinitescroller from 'molbio/maps/linear/InfiniteScroller';
import {InfiniteScrollbar, InfiniteScroller, ScrollData} from 'molbio/maps/linear/InfiniteScroller';
import SequenceAlignmentTabState from 'molbio/tabstates/SequenceAlignmentTabState';
import addTestAttrsToDOMElement from 'test/lib/cypress/addTestAttrsToDOMElement';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import {verticallyScrollItemIntoView} from 'util/Scroll';

interface CommonSequenceAlignmentRowsViewInterface extends MinimalAlignmentRowsView {
  handleAlignmentSort(): void;
  jumpToIndex(index: number): void;
}

interface SequenceAlignmentRowsViewInitOptions {
  model: SequenceAlignmentTabState;
  tabView: SequenceAlignmentTabView;
  saveOrderHandler: (cids: Array<string>) => void;
}

class SequenceAlignmentRowsView
  extends Backbone.View<SequenceAlignmentTabState>
  implements CommonSequenceAlignmentRowsViewInterface
{
  saveOrderHandler!: (cids: Array<string>) => void;
  views!: Array<SequenceAlignmentRowView>;
  tabView?: SequenceAlignmentTabView;

  static initClass(): void {
    this.prototype.className = 'scrolling-alignment-rows';
  }

  constructor(options: SequenceAlignmentRowsViewInitOptions) {
    super(options);
  }

  initialize(options: SequenceAlignmentRowsViewInitOptions): void {
    this.saveOrderHandler = options.saveOrderHandler;
    this.views = [];
    this.tabView = options.tabView;
  }

  die(): void {
    for (let i = 0; i < this.views.length; i++) {
      const view = this.views[i];
      view.die();
    }
    // see https://jqueryui.com/sortable/
    this.$el.find('.sortable-wrapper').sortable('destroy');
  }

  update(): void {
    const $wrapper = this.$el.find('.sortable-wrapper');
    $wrapper.empty();

    // Remove all rendered views
    for (const view of this.views) {
      view.remove();
    }
    this.views = [];
    // Re-render them.
    for (const alignmentMapState of this.model.get('mapstates').models) {
      if (
        alignmentMapState.get('alignment').get('type') !== this.model.get('analysis')!.get('alignmentType')
      ) {
        const view = new SequenceAlignmentRowView({
          model: alignmentMapState,
          tabstate: this.model,
          tabView: this.tabView,
        });
        const {el} = view.render();
        $(el).attr('data-id', alignmentMapState.get('alignment').cid);
        $(el).data('data-view', view);
        addTestAttrsToDOMElement(el, {component: 'AlignmentRow', element: 'root'});
        $wrapper.append(el);
        this.views.push(view);
      }
    }
  }

  showRange(left: number | null = null): void {
    for (let i = 0; i < this.views.length; i++) {
      const view = this.views[i];
      view.showRange(left);
    }
  }

  resizeSVGs(): void {
    for (let i = 0; i < this.views.length; i++) {
      const view = this.views[i];
      view.setSVGWidth();
    }
  }

  _saveOrder(): void {
    const cids = this.$el
      .find('.alignment-row')
      .toArray()
      .map((row) => $(row).attr('data-id'));
    this.saveOrderHandler(cids);
  }

  handleAlignmentSort(): void {
    // Map each slice to an index.
    const cidOrder: {[cid: string]: number} = {};
    this.model
      .get('analysis')!
      .get('alignments')
      .models.forEach((model: Alignment, i: number) => {
        cidOrder[model.cid] = i;
      });
    // Leave the top view alone.
    // eslint-disable-next-line no-restricted-syntax
    const alignedViews = _.filter(this.views, (view) => cidOrder[view.$el.data('id')] > 0);
    const $viewEls = $(alignedViews.map((view) => view.el));
    const $parent = $viewEls.parent();
    // Sort slices by index (ignore JQuery typing)
    // @ts-expect-error
    $viewEls.detach().sort((nodeA, nodeB) => {
      const nodeAOrder = cidOrder[$(nodeA).data('id')];
      const nodeBOrder = cidOrder[$(nodeB).data('id')];
      return nodeAOrder - nodeBOrder;
    });
    $parent.append($viewEls);
  }

  render(): SequenceAlignmentRowsView {
    // Make alignment views sortable.
    const $wrapper = this.$el.find('.sortable-wrapper');
    // see https://jqueryui.com/sortable/
    $wrapper.sortable({
      items: '> .alignment-row',
      handle: '.handle',
      // @ts-expect-error: unexpected and possibly unused argument
      cursorOffset: `url(${staticURL('/static/img/closedhand.cur')}), pointer`,
      forcePlaceholderSize: true,
      // TODO fill in
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      start(e: any, ui: any): number {
        return ui.placeholder.height(ui.item.height());
      },
      scroll: false,
      axis: 'y',
      stop: () => {
        this._saveOrder();
      },
    });
    $wrapper.mousedown(() => {
      // assume document is not null
      // @ts-expect-error
      document.activeElement.blur();
    });

    $wrapper.on('sortstop', (e, ui) => {
      const dataView = $(ui.item).data('data-view');
      if (dataView) {
        dataView.showRange();
      }
    });
    this.update();
    return this;
  }

  jumpToIndex(index: number): void {
    const mapStateEl = this.views[index].$el[0];
    // Don't use native `scrollIntoView` even with {behavior: smooth} - it doesn't animate as nicely
    verticallyScrollItemIntoView(this.el, mapStateEl);
  }
}
SequenceAlignmentRowsView.initClass();

class AlignmentRowSource extends infinitescroller.ItemSource<SequenceAlignmentRowView> {
  model!: SequenceAlignmentTabState;
  tabView!: SequenceAlignmentTabView;
  indexOfFixed!: number;
  // TODO: Implement updateItem so we can reuse sliceviews

  initialize(tabstate: SequenceAlignmentTabState, tabView: SequenceAlignmentTabView): void {
    this.model = tabstate;
    this.tabView = tabView;
    const mapstates = tabstate.get('mapstates');
    const analysis = tabstate.get('analysis')!;
    for (let i = 0; i < mapstates.models.length; i++) {
      const ms = mapstates.models[i];
      if (ms.get('alignment').get('type') === analysis.get('alignmentType')) {
        this.indexOfFixed = i;
        break;
      }
    }
    assertAndContinue(this.indexOfFixed != null, 'Did not find fixed alignment row');
    this.updateLength();
    this.listenTo(mapstates, 'add remove reset', this.updateLength.bind(this));
  }

  updateLength(): void {
    this.set('length', this.model.get('mapstates').length - 1);
  }

  getItem(index: number): SequenceAlignmentRowView {
    const mapstates = this.model.get('mapstates');
    if (index >= this.indexOfFixed) {
      index++;
    }
    const mapstate = mapstates.models[index];
    const item = new SequenceAlignmentRowView({model: mapstate, tabstate: this.model, tabView: this.tabView});
    item.$el.attr('data-id', mapstate.get('alignment').cid);
    // TODO: fix & remove ignore
    // @ts-expect-error
    item.$el.attr('data-view', item);
    setTestAttrs(item.$el, {component: 'AlignmentRow', element: 'root'});
    return item;
  }

  estimatedHeight(): number {
    return 30;
  }
}

interface InfiniteAlignmentRowsViewInitOptions {
  model: SequenceAlignmentTabState;
  tabView: SequenceAlignmentTabView;
}
class InfiniteSequenceAlignmentRowsView
  extends Backbone.View<SequenceAlignmentTabState>
  implements CommonSequenceAlignmentRowsViewInterface
{
  hasAttached?: boolean;
  infiniteScroller!: InfiniteScroller<SequenceAlignmentRowView>;
  infiniteScrollbar!: InfiniteScrollbar;
  itemSource!: AlignmentRowSource;
  scrollData!: ScrollData;

  static initClass(): void {
    this.prototype.className = 'infinite-alignment-rows';
  }

  constructor(options: InfiniteAlignmentRowsViewInitOptions) {
    super(options);
  }

  initialize(options: InfiniteAlignmentRowsViewInitOptions): void {
    this.scrollData = new infinitescroller.ScrollData();
    this.itemSource = new AlignmentRowSource(this.model, options.tabView);
    this.infiniteScroller = new infinitescroller.InfiniteScroller({
      tabstate: this.model,
      itemSource: this.itemSource,
      scrollData: this.scrollData,
      spacing: 0,
    });
    this.infiniteScrollbar = new infinitescroller.InfiniteScrollbar({
      tabstate: this.model,
      model: this.scrollData,
      fullHeight: () => this.$el.height(),
    });
    this.listenTo(this.itemSource, 'change:length', this.rerenderRows.bind(this));
  }

  die(): void {
    // TODO
  }

  rerenderRows(): void {
    const topIndex = this.infiniteScroller.firstRendered()!.index();
    this.infiniteScroller.bootstrap({index: topIndex, flush: true, topAnchor: 10000});
    this.infiniteScroller.jumpToIndex(topIndex, 0);
  }

  resizeSVGs(): void {
    for (let i = 0; i < this.infiniteScroller.renderedItems.length; i++) {
      const item = this.infiniteScroller.renderedItems[i];
      const view = item.item();
      view.setSVGWidth();
    }
  }

  showRange(left: number | null = null): void {
    for (let i = 0; i < this.infiniteScroller.renderedItems.length; i++) {
      const item = this.infiniteScroller.renderedItems[i];
      const view = item.item();
      view.showRange(left);
    }
  }

  handleAlignmentSort(): void {
    this.infiniteScroller.bootstrap({index: 0, flush: true, topAnchor: 0});
    this.infiniteScroller.jumpToIndex(0, 0);
  }

  update(): void {
    if (!this.hasAttached) {
      const $scrollWrapper = $('<div class="reset scroll-wrapper"></div>');
      this.$el.append($scrollWrapper);
      $scrollWrapper.append(this.infiniteScroller.render().el);
      $scrollWrapper.append(this.infiniteScrollbar.render().el);
      this.hasAttached = true;
    }
  }

  render(): InfiniteSequenceAlignmentRowsView {
    this.update();
    return this;
  }

  jumpToIndex(index: number): void {
    this.infiniteScroller.jumpToIndex(index);
  }
}
InfiniteSequenceAlignmentRowsView.initClass();

export {InfiniteSequenceAlignmentRowsView, SequenceAlignmentRowsView};
